import { Mic, Settings2, Video, Volume2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import type { BackgroundMode } from '../media/backgroundProcessor';
import type { CameraQualitySetting } from './publishQuality';
import { BackgroundModePicker } from './BackgroundPickers';
import { DeviceSelect } from './DeviceSelect';
import { deviceOptions, NO_DEVICE } from './prejoinState';
import type { DeviceOption, InputDeviceInfoLike } from './prejoinState';

/**
 * The state side of the quick-settings binding: what the panel shows. Values mirror the
 * device-preferences store (the session request the join wrote); the panel is a shortcut
 * surface, never a second source of truth.
 */
export interface QuickSettingsState {
  readonly micDeviceId: string | null;
  readonly cameraDeviceId: string | null;
  readonly speakerDeviceId: string | null;
  readonly camQuality: CameraQualitySetting;
  readonly bgMode: BackgroundMode;
}

/**
 * The action side of the binding: the room page owns every application path (live microphone
 * re-acquisition, camera off/on cycle, sink switch, the store subscription that drives the
 * background machine). The panel only reports intent.
 */
export interface QuickSettingsActions {
  readonly onMicDevice: (deviceId: string | null) => void;
  readonly onCameraDevice: (deviceId: string | null) => void;
  readonly onSpeakerDevice: (deviceId: string | null) => void;
  readonly onCamQuality: (quality: CameraQualitySetting) => void;
  readonly onBgMode: (mode: BackgroundMode) => void;
  readonly onOpenEffects: () => void;
}

/** Presentation of the shared panel: the desktop popover box or the mobile bottom sheet. */
export type QuickSettingsVariant = 'popover' | 'sheet';

export interface QuickSettingsPanelProps {
  readonly state: QuickSettingsState;
  readonly actions: QuickSettingsActions;
  /** Closes the surface (the bars own the open state); the effects entry always closes first. */
  readonly onClose: () => void;
  /** Device enumeration override for tests; defaults to navigator.mediaDevices. */
  readonly enumerateDevices?: (() => Promise<readonly InputDeviceInfoLike[]>) | undefined;
  /**
   * Capability override for tests: whether this engine can switch the audio output at all
   * (HTMLMediaElement.setSinkId). Defaults to the runtime detection - iOS Safari carries no
   * setSinkId, so the speaker picker degrades to a disabled control with an honest hint
   * instead of accepting picks that can never apply.
   */
  readonly sinkSelectionSupported?: boolean | undefined;
  /** Popover (default, desktop sizing) or sheet (mobile bottom sheet, larger touch targets). */
  readonly variant?: QuickSettingsVariant | undefined;
}

/** The Permissions API surface this module needs, kept optional (Safari gaps). */
interface PermissionStatusLike {
  addEventListener(type: 'change', listener: () => void): void;
  removeEventListener(type: 'change', listener: () => void): void;
}
interface PermissionsManagerLike {
  query?: (descriptor: { name: string }) => Promise<PermissionStatusLike>;
}

/**
 * Whether this engine can route remote audio to a picked output device at all. iOS Safari
 * (and jsdom) expose no setSinkId: there the speaker picker renders disabled with an honest
 * hint - never a control that silently ignores its own picks. Exported so the /settings page
 * can adopt the same degrade.
 */
export function speakerSelectionSupported(): boolean {
  return (
    typeof HTMLMediaElement !== 'undefined' &&
    typeof HTMLMediaElement.prototype.setSinkId === 'function'
  );
}

/**
 * The camera-quality chips the panel offers, in panel order. The store also carries 'low'
 * (the /settings page keeps all four); the panel is the quick surface for the common ladder:
 * auto / 720p / 1080p.
 */
const QUICK_CAMERA_QUALITIES: readonly CameraQualitySetting[] = ['auto', 'medium', 'high'];

/**
 * The quick-settings panel: personal devices and camera look-and-feel in one compact surface,
 * reachable from the room's control bars by staff AND participants (nothing here needs a role).
 * Every control reuses an existing surface's state and vocabulary - the prejoin DeviceSelect
 * per device kind, the device-preferences store for the background mode and camera quality,
 * the effects dialog for everything deeper. Device ids follow the /settings semantics: the
 * «System default» option means null (the SDK picks the default device), never "device off" -
 * an in-call device switch re-acquires, it does not unpublish.
 *
 * The desktop bars anchor it as a popover; the mobile bars mount the same panel inside the
 * bottom Sheet (full-width, scrollable, scrim dismissal) where a 292px floating popover was
 * cramped and clipped on short/landscape viewports.
 */
export function QuickSettingsPanel({
  state,
  actions,
  onClose,
  enumerateDevices,
  sinkSelectionSupported,
  variant = 'popover',
}: QuickSettingsPanelProps): JSX.Element {
  const { t } = useTranslation();
  const [cameraOptions, setCameraOptions] = useState<readonly DeviceOption[]>([]);
  const [micOptions, setMicOptions] = useState<readonly DeviceOption[]>([]);
  const [speakerOptions, setSpeakerOptions] = useState<readonly DeviceOption[]>([]);
  const sheet = variant === 'sheet';
  const speakerSupported = sinkSelectionSupported ?? speakerSelectionSupported();

  // One enumeration on mount plus a LIVE reload: devicechange covers hotplug (headsets docked
  // mid-call) and the camera/microphone permissionchange covers the mobile grant flow, where
  // the first enumeration carries empty labels and the labels arrive only after the grant.
  // Skipped entirely when the enumeration is a test stub (deterministic unit suites).
  useEffect(() => {
    let cancelled = false;
    const enumerate =
      enumerateDevices ??
      ((): Promise<readonly InputDeviceInfoLike[]> => {
        const media = navigator.mediaDevices;
        if (media === undefined) {
          return Promise.resolve([]);
        }
        return media.enumerateDevices();
      });
    const reload = (): void => {
      void enumerate()
        .then((devices) => {
          if (cancelled) {
            return;
          }
          setCameraOptions(deviceOptions(devices, 'videoinput'));
          setMicOptions(deviceOptions(devices, 'audioinput'));
          setSpeakerOptions(deviceOptions(devices, 'audiooutput'));
        })
        .catch(() => {
          // No device list (blocked permission, insecure context): keep the current lists.
        });
    };
    reload();
    const removers: Array<() => void> = [];
    const onDeviceChange = (): void => {
      reload();
    };
    if (enumerateDevices === undefined) {
      const media = navigator.mediaDevices;
      if (media !== undefined && typeof media.addEventListener === 'function') {
        media.addEventListener('devicechange', onDeviceChange);
        removers.push(() => {
          media.removeEventListener('devicechange', onDeviceChange);
        });
      }
      const manager = navigator.permissions as PermissionsManagerLike | undefined;
      if (manager !== undefined && typeof manager.query === 'function') {
        for (const name of ['camera', 'microphone']) {
          void manager
            .query({ name })
            .then((status) => {
              if (cancelled) {
                return;
              }
              status.addEventListener('change', onDeviceChange);
              removers.push(() => {
                status.removeEventListener('change', onDeviceChange);
              });
            })
            .catch(() => {
              // Engines without permission queries for media kinds: devicechange still fires.
            });
        }
      }
    }
    return () => {
      cancelled = true;
      for (const remove of removers.splice(0)) {
        remove();
      }
    };
    // onDeviceChange is stable (a reload closure per effect run); enumerateDevices gates it.
  }, [enumerateDevices]);

  // Escape closes the surface (role="dialog" affordance of the popover variant): the bar keeps
  // no focus trap, so the listener rides the document for the popover's lifetime (it unmounts
  // on close). The sheet variant relies on the Radix dialog's own Escape handling.
  useEffect(() => {
    if (sheet) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, sheet]);

  const defaultLabel = t('quick.device_default');
  const micSelect = useMemo(
    (): JSX.Element => (
      <DeviceSelect
        id="quick-microphone"
        label={t('prejoin.mic_label')}
        icon={Mic}
        value={state.micDeviceId ?? NO_DEVICE}
        options={micOptions}
        noDeviceLabel={defaultLabel}
        testid="quick-mic-select"
        className={sheet ? 'h-11 text-sm' : undefined}
        onChange={(deviceId) => {
          actions.onMicDevice(deviceId === NO_DEVICE ? null : deviceId);
        }}
      />
    ),
    [t, state.micDeviceId, micOptions, defaultLabel, actions, sheet],
  );
  const cameraSelect = useMemo(
    (): JSX.Element => (
      <DeviceSelect
        id="quick-camera"
        label={t('prejoin.camera_label')}
        icon={Video}
        value={state.cameraDeviceId ?? NO_DEVICE}
        options={cameraOptions}
        noDeviceLabel={defaultLabel}
        testid="quick-camera-select"
        className={sheet ? 'h-11 text-sm' : undefined}
        onChange={(deviceId) => {
          actions.onCameraDevice(deviceId === NO_DEVICE ? null : deviceId);
        }}
      />
    ),
    [t, state.cameraDeviceId, cameraOptions, defaultLabel, actions, sheet],
  );
  const speakerSelect = useMemo(
    (): JSX.Element => (
      <DeviceSelect
        id="quick-speakers"
        label={t('prejoin.speakers_label')}
        icon={Volume2}
        value={state.speakerDeviceId ?? NO_DEVICE}
        options={speakerOptions}
        noDeviceLabel={defaultLabel}
        testid="quick-speaker-select"
        className={sheet ? 'h-11 text-sm' : undefined}
        // Engines without setSinkId: the pick could never apply, so the control renders
        // disabled with an honest hint below - never a dead selectable.
        disabled={!speakerSupported}
        onChange={(deviceId) => {
          actions.onSpeakerDevice(deviceId === NO_DEVICE ? null : deviceId);
        }}
      />
    ),
    [t, state.speakerDeviceId, speakerOptions, defaultLabel, actions, sheet, speakerSupported],
  );

  return (
    <div
      data-testid="quick-settings-panel"
      className={cn('text-zinc-100', sheet ? 'p-4' : 'p-3')}
    >
      <div className="mb-2 flex items-center gap-1.5">
        <Settings2 aria-hidden className="size-3.5 text-zinc-400" />
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-400">
          {t('controls.quick_settings')}
        </p>
        <button
          type="button"
          data-testid="quick-settings-close"
          aria-label={t('quick.close_aria')}
          title={t('quick.close_aria')}
          onClick={onClose}
          className="ml-auto grid size-6 place-items-center rounded text-zinc-400 transition-colors hover:bg-white/10 hover:text-white"
        >
          <span aria-hidden>×</span>
        </button>
      </div>

      {/* Devices: one picker per kind, the prejoin select reused in full (native element keeps
          the picker operable from tests and e2e suites). */}
      <div className={cn('flex flex-col', sheet ? 'gap-3' : 'gap-2')}>
        {micSelect}
        {cameraSelect}
        {speakerSelect}
        {speakerSupported ? (
          <p className="text-[10.5px] leading-snug text-zinc-500">{t('effects.mic_restart_hint')}</p>
        ) : (
          <p className="text-[10.5px] leading-snug text-zinc-500">
            {t('quick.speaker_unsupported')}
          </p>
        )}
      </div>

      {/* Camera quality: the stored publish override; a pinned tier applies from the NEXT
          session (the session-tier invariant - no capture renegotiation mid-call). */}
      <div className={cn(sheet ? 'mt-4' : 'mt-3')}>
        <p className="mb-1.5 text-[11px] font-medium text-zinc-400">{t('settings.cam_quality')}</p>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('settings.cam_quality')}>
          {QUICK_CAMERA_QUALITIES.map((quality) => (
            <button
              key={quality}
              type="button"
              data-testid={`quick-cam-quality-${quality}`}
              aria-pressed={state.camQuality === quality}
              onClick={() => {
                actions.onCamQuality(quality);
              }}
              className={cn(
                'rounded-lg border px-2.5 py-1 text-[12px] font-medium transition-colors',
                sheet && 'py-1.5',
                state.camQuality === quality
                  ? 'border-emerald-400/60 bg-emerald-500/20 text-emerald-200'
                  : 'border-white/10 text-zinc-300 hover:bg-white/10',
              )}
            >
              {t(`settings.cam_quality_${quality}`)}
            </button>
          ))}
        </div>
        <p className="mt-1 text-[10.5px] leading-snug text-zinc-500">
          {t('settings.cam_quality_note')}
        </p>
      </div>

      {/* Background: the same picker the effects dialog renders; a change applies LIVE through
          the preference-store subscription (the replaceTrack machine). */}
      <div className={cn(sheet ? 'mt-4' : 'mt-3')}>
        <p className="mb-1.5 text-[11px] font-medium text-zinc-400">{t('settings.bg_mode')}</p>
        <BackgroundModePicker
          mode={state.bgMode}
          onMode={actions.onBgMode}
          testidPrefix="quick-bg-mode"
        />
      </div>

      {/* Effects: the entry to the full dialog (mic processing, filters, masks, blur strength). */}
      <button
        type="button"
        data-testid="quick-open-effects"
        onClick={() => {
          onClose();
          actions.onOpenEffects();
        }}
        className={cn(
          'flex h-8 w-full items-center justify-center gap-1.5 rounded-lg border border-white/10 text-[12.5px] font-medium text-zinc-200 transition-colors hover:bg-white/10',
          sheet && 'mt-4 h-11',
        )}
      >
        {t('controls.effects')}
      </button>
    </div>
  );
}

export interface QuickSettingsPopoverProps {
  readonly state: QuickSettingsState;
  readonly actions: QuickSettingsActions;
  /** Closes the popover (the bars own the open state); the effects entry always closes first. */
  readonly onClose: () => void;
  /** Device enumeration override for tests; defaults to navigator.mediaDevices. */
  readonly enumerateDevices?: (() => Promise<readonly InputDeviceInfoLike[]>) | undefined;
  /** Capability override for the speaker picker (see QuickSettingsPanelProps). */
  readonly sinkSelectionSupported?: boolean | undefined;
}

/**
 * The desktop presentation of the quick settings: the shared panel in its compact floating
 * box, right-anchored above the control bar by the bars themselves. The mobile bars render
 * the same panel inside a bottom Sheet instead (the sheet variant).
 */
export function QuickSettingsPopover({
  state,
  actions,
  onClose,
  enumerateDevices,
  sinkSelectionSupported,
}: QuickSettingsPopoverProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      data-testid="quick-settings-popover"
      role="dialog"
      aria-label={t('controls.quick_settings')}
      className="w-[292px] max-w-[calc(100vw-16px)] rounded-xl border border-white/10 bg-zinc-900 shadow-xl shadow-black/50 backdrop-blur"
    >
      <QuickSettingsPanel
        state={state}
        actions={actions}
        onClose={onClose}
        enumerateDevices={enumerateDevices}
        sinkSelectionSupported={sinkSelectionSupported}
      />
    </div>
  );
}
